import React, { useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";

import { disconnectWhatsApp } from "../../whatsappApi";

/**
 * DisconnectModal — confirms disconnecting a practice's WhatsApp account.
 *
 * Destructive confirmation per /CLAUDE.md §1.8.1: warning alert at the top of
 * the body, `danger` commit button with a fixed minWidth, matching
 * `outline-secondary` Cancel, spinner swap while submitting.
 *
 * No free-text field, so the §1.8.1 close-confirmation interstitial does not
 * apply — an empty close should just close.
 */
export default function DisconnectModal({
  show,
  onHide,
  practiceNumber,
  practiceName,
  onDisconnected,
  showToast,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await disconnectWhatsApp(practiceNumber);
      showToast?.("success", "WhatsApp disconnected.");
      onDisconnected?.();
      onHide?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || "Could not disconnect WhatsApp.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Disconnect WhatsApp{practiceName ? `: ${practiceName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="warning">
          Appointment reminders will <strong>stop immediately</strong>, and
          patients who message this number will get{" "}
          <strong>no reply and no notification</strong>.
        </Alert>

        <p className="mb-0">
          Your conversation history is kept and stays visible in PractoSync. You
          can reconnect at any time.
        </p>

        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="danger"
          style={{ minWidth: 140 }}
          onClick={confirm}
          disabled={submitting}
        >
          {submitting ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <>
              <i className="fa-solid fa-link-slash me-2" />
              Disconnect
            </>
          )}
        </Button>
        <Button
          variant="outline-secondary"
          style={{ minWidth: 140 }}
          onClick={onHide}
          disabled={submitting}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
